<template>
    <div class="upload-container">
        <el-card class="upload-card">
            <template #header>
                <div class="header-title">
                    <el-icon class="title-icon">
                        <Picture />
                    </el-icon>
                    <span>背景图设置</span>
                </div>
            </template>

            <div class="upload-area">
                <!-- 当前背景图预览 -->
                <div class="preview-area">
                    <div class="preview-title">
                        <span v-if="previewUrl">当前背景图</span>
                        <span v-else>选择后预览</span>
                    </div>
                    <el-image :src="previewUrl || defaultPreview" fit="cover"
                        :preview-src-list="previewUrl ? [previewUrl] : []" class="preview-image">
                        <template #error>
                            <div class="image-error">暂无图片</div>
                        </template>
                    </el-image>
                </div>

                <!-- 上传区域 -->
                <div class="upload-controls">
                    <el-upload class="avatar-uploader" :action="uploadUrl" :headers="headers" :show-file-list="false"
                        :before-upload="beforeUpload"
                        ref="uploadRef">
                        <el-button size="small" type="primary" :loading="uploading">
                            <el-icon>
                                <Upload />
                            </el-icon>
                            <span>选择图片</span>
                        </el-button>
                        <div class="upload-tip">
                            <el-icon>
                                <InformationFilled />
                            </el-icon>
                            <span>支持 JPG、PNG 格式，建议尺寸 1920x1080</span>
                        </div>
                    </el-upload>

                    <!-- 图片已选择提示 -->
                    <div class="selected-tip" v-if="selectedFile">
                        <el-icon>
                            <Check />
                        </el-icon>
                        <span>已选择: {{ selectedFile.name }}</span>
                    </div>
                </div>
            </div>

            <!-- 操作按钮 -->
            <div class="action-buttons">
                <el-button type="primary" @click="saveBackground" :loading="saving"
                    :disabled="!selectedFile && !previewUrl">
                    保存设置
                </el-button>
            </div>
        </el-card>

        <!-- 上传进度条 -->
        <el-progress v-if="showProgress" :percentage="progress" status="success">
        </el-progress>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import {
    Picture,
    Upload,
    Loading,
    InformationFilled,
    Check
} from '@element-plus/icons-vue';
import axios from 'axios';
import { ASSET_BASE } from '../../../util/assetBase';

// API配置
const uploadUrl = '/adminapi/setting/img';
const getCurrentBackgroundUrl = '/adminapi/setting/list';

// 状态管理
const previewUrl = ref('');
const uploading = ref(false);
const saving = ref(false);
const showProgress = ref(false);
const progress = ref(0);
const selectedFile = ref(null);
const uploadRef = ref(null);
const defaultPreview = '';

// 获取当前用户信息和token
const token = localStorage.getItem('token') || '';

// 请求头配置
const headers = reactive({
    'Authorization': `Bearer ${token}`
});

// 生命周期钩子
onMounted(() => {
    fetchCurrentBackground();
});

// 获取当前背景图
const fetchCurrentBackground = async () => {
    try {
        const response = await axios.get(getCurrentBackgroundUrl);
        if (response.status === 200) {
            const settingData = response.data.results[0] || {};
            console.log(settingData.page)
            previewUrl.value = settingData?.page ? ASSET_BASE + settingData.page : '';
        }
    } catch (error) {
        console.error('获取背景图错误:', error);
        ElMessage.error('获取背景图失败，请稍后重试');
    }
};

// 上传前校验
const beforeUpload = (file) => {
    const isJPG = file.type === 'image/jpeg';
    const isPNG = file.type === 'image/png';
    const isLt2M = file.size / 1024 / 1024 < 10;

    if (!isJPG && !isPNG) {
        ElMessage.error('上传图片只能是 JPG/PNG 格式!');
        return false;
    }

    if (!isLt2M) {
        ElMessage.error('上传图片大小不能超过 10MB!');
        return false;
    }

    // 阻止自动上传
    selectedFile.value = file;

    // 创建本地预览
    const reader = new FileReader();
    reader.onload = (e) => {
        previewUrl.value = e.target.result;
    };
    reader.readAsDataURL(file);

    return false; // 返回false阻止自动上传
};

// 处理上传成功
const handleUploadSuccess = (response) => {
    console.log(response.code)
    if (response.code === 0) {
        // previewUrl.value = response.data.url;
        selectedFile.value = null;
        ElMessage.success('背景图上传成功');
    } else {
        handleUploadError(response.message)
        ElMessage.error(response.message || '上传失败');
    }
    uploading.value = false;
    showProgress.value = false;
};

// 处理上传失败
const handleUploadError = (error) => {
    console.error('上传错误:', error);
    ElMessage.error('上传失败，请稍后重试');
    uploading.value = false;
    showProgress.value = false;
};

// 保存背景图设置
const saveBackground = async () => {
    if (!selectedFile.value && !previewUrl.value) {
        ElMessage.warning('请先选择背景图');
        return;
    }

    saving.value = true;

    try {
        if (selectedFile.value) {
            // 手动触发上传
            const formData = new FormData();
            formData.append('file', selectedFile.value);
            
            const response = await axios.post(uploadUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                },
                onUploadProgress: (progressEvent) => {
                    progress.value = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    showProgress.value = true;
                }
            });

            if (response.data.code === 0) {
                previewUrl.value = ASSET_BASE + response.data.data.url;
                ElMessage.success('背景图上传成功');
            } else {
                ElMessage.error(response.data.message || '上传失败');
            }
        } else {
            // 没选新图片就不再打接口了 —— 后端收不到文件会把背景图清空（还提示「设置成功」），
            // 以前就是这儿把主人的背景图弄没的。当前这张已经生效了，不用再存一次。
            ElMessage.info('没有选新的图片，当前背景图不用再保存一次');
        }
    } catch (error) {
        console.error('保存背景图错误:', error);
        ElMessage.error('保存失败，请稍后重试');
    } finally {
        saving.value = false;
        showProgress.value = false;
    }
};
</script>


<style scoped lang="scss">
.upload-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
}

.upload-card {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 20px;
}

.header-title {
    display: flex;
    align-items: center;
    font-size: 1.2rem;
    font-weight: 600;

    .title-icon {
        margin-right: 8px;
        color: #409eff;
    }
}

.upload-area {
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.preview-area {
    margin-bottom: 30px;
    width: 100%;
    max-width: 600px;

    .preview-title {
        margin-bottom: 10px;
        font-weight: 500;
        color: #606266;
    }

    .preview-image {
        width: 100%;
        height: 300px;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        background-color: #f5f7fa;
    }

    .image-error {
        width: 100%;
        height: 300px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-color: #f5f7fa;
        border-radius: 8px;
        color: #909399;

        .empty-image-icon {
            font-size: 60px;
            margin-bottom: 15px;
            color: #dcdfe6;
        }

        .empty-image-text {
            font-size: 14px;
        }
    }
}

.upload-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 400px;
}

.upload-tip {
    margin-top: 10px;
    color: #909399;
    font-size: 12px;

    .el-icon {
        margin-right: 4px;
    }
}

.selected-tip {
    margin-top: 15px;
    color: #67c23a;
    font-size: 14px;
    display: flex;
    align-items: center;

    .el-icon {
        margin-right: 6px;
    }
}

.action-buttons {
    display: flex;
    justify-content: center;
    padding: 20px;
}
</style>